import { Alert, Ref, TextArea, TextAreaProps } from "@fluentui/react-northstar";
import { FieldDescriptor } from "@shopify/react-form-state";
import React, { TextareaHTMLAttributes, useCallback, useEffect } from "react";

import { FieldLabel, FieldLabelProps } from ".";

type Props = {
  label?: React.ReactNode;
  field: FieldDescriptor<string>;
  labelProps?: Partial<FieldLabelProps>;
  style?: React.CSSProperties;
  tooltip?: React.ReactNode;
  className?: string;
  autoExpandHeight?: boolean;
} & TextareaHTMLAttributes<HTMLTextAreaElement> &
  TextAreaProps;

export const TextAreaField: React.FC<Props> = props => {
  const {
    field,
    label,
    style,
    className,
    tooltip,
    labelProps,
    autoExpandHeight,
    onInput,
    ...textAreaProps
  } = props;

  if (field === undefined) {
    throw new Error(`Invalid field for TextAreaField ${className} ${label}`);
  }

  let textArea: HTMLTextAreaElement | null = null;

  const refreshTextAreaHeight = useCallback(() => {
    if (!textArea) {
      return;
    }

    textArea.style.height = "auto";
    textArea.style.height = textArea.scrollHeight + "px";
  }, [textArea]);

  useEffect(() => {
    if (!autoExpandHeight) {
      return;
    }

    // set initial height of textarea on mount
    refreshTextAreaHeight();
  }, [autoExpandHeight, refreshTextAreaHeight]);

  return (
    <div style={style} className={className}>
      {label && (
        <FieldLabel
          label={label}
          tooltip={tooltip}
          size="medium"
          disabled={textAreaProps.disabled}
          {...labelProps}
        />
      )}
      <Ref
        innerRef={ref => {
          textArea = ref as HTMLTextAreaElement;
        }}
      >
        <TextArea
          onChange={e => {
            const newValue = (e.currentTarget as HTMLInputElement).value;
            if (newValue !== field.value) {
              field.onChange((newValue as any) || "");
            }
          }}
          value={field.value}
          id={field.name}
          styles={{
            marginTop: ".25rem",
            opacity: textAreaProps.disabled ? 0.5 : 1,
            ...(autoExpandHeight && {
              overflowY: "hidden",
            }),
          }}
          onInput={
            !autoExpandHeight
              ? onInput
              : (event: React.FormEvent<HTMLTextAreaElement>) => {
                  if (onInput) {
                    onInput(event);
                  }
                  refreshTextAreaHeight();
                }
          }
          {...textAreaProps}
        />
      </Ref>

      {field.error && <Alert content={field.error} danger attached="bottom" />}
    </div>
  );
};
